// Entry point of the admin SPA (ADR-032). Loads the session from `GET /v1/me`, then renders the
// route the address names: public routes (SCR-002) bare, every other one inside the shell once
// the principal may open it (FLOW-002: sign-in redirect with return path, "not available to your
// role", not found). ROUTES and the landing choice are `app-routes.ts` (TASK-140), which
// concatenates every screen module's routes; NAV order (router/route.ts's NAV_LABELS) is what
// `navEntries` renders in, not that array's order.
import { render } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { HostContext, ScopeProvider, type Host } from "@nexus/ui";
import { ROUTES, landingPath } from "./app-routes";
import { createApiClient } from "@nexus/ui";
import { ApiContext } from "@nexus/ui";
import { NetworkError, type ApiError } from "@nexus/ui";
import { HOME_PATH } from "./screens/home/model";
import { SIGN_IN_PATH, signInPath } from "./screens/signin/model";
import { legacyRedirect } from "./router/legacy";
import { createRouter, navEntries, resolve, RouterContext } from "./router/router";
import { articleHref } from "./help/articles";
import { helpTopic } from "./help/topics";
import { createCsrfToken } from "@nexus/ui";
import { loadSession, scopeOf, SessionContext, signOut, type SessionServices, type SessionState } from "./session/session";
import { breadcrumbTrail, documentTitle, type Crumb } from "./ui/breadcrumb";
import { CustomerDirectoryProvider } from "./ui/customer-directory";
import { environmentFromHost } from "./ui/environment";
import { HelpProvider } from "./ui/help";
import { Lockup, LogoMark } from "./ui/logo";
import { BreadcrumbContext, Shell } from "./ui/shell";
import { ErrorBanner, Forbidden, Loading, NotFound } from "@nexus/ui";

const csrf = createCsrfToken();
const client = createApiClient({ csrf });
const router = createRouter(window);
// The host the shared `@nexus/ui` components reach the shell through (ADR-101): the router for
// navigation, the Help section for a topic's article.
const adminHost: Host = {
  kind: "allcode-admin",
  basePath: "",
  navigate: (href) => router.navigate(href),
  // A help topic's article lives in the Help section; `helpTopic` maps the topic id to it.
  helpHref: (topic) => {
    const t = helpTopic(topic);
    return t ? articleHref(t.article, t.section) : "/help";
  },
};
const environment = environmentFromHost(window.location.hostname);

function App({ initial }: { initial: SessionState }) {
  const [state, setState] = useState(initial);
  const [location, setLocation] = useState(router.location());
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [crumbTail, setCrumbTail] = useState<readonly Crumb[] | null>(null);

  useEffect(() => router.subscribe(setLocation), []);

  // Renamed screens (Orgs → Customers, Billing → Invoices, …): an old address becomes its new one.
  const redirect = legacyRedirect(location);
  useEffect(() => {
    if (redirect !== null) router.navigate(redirect, { replace: true });
  }, [redirect]);
  useEffect(
    () =>
      client.onSessionExpired(() => {
        const here = router.location();
        setState({ status: "signed-out" });
        if (here.pathname !== SIGN_IN_PATH) router.navigate(signInPath("expired", here.pathname + here.search), { replace: true });
      }),
    [],
  );

  const session = state.status === "signed-in" ? state.session : null;
  // The shared screens (`@nexus/ui`) read the principal from the scope, not from SessionContext.
  const scope = useMemo(() => (session ? scopeOf(session.me) : null), [session]);
  const resolution = resolve(ROUTES, location, session);
  const isPublic = resolution.kind === "screen" && resolution.route.permission === "public";

  // Without a session every non-public address — known or not — leads to sign-in and back.
  useEffect(() => {
    if (isPublic) return;
    if (state.status === "no-access") router.navigate(signInPath("no_access", null), { replace: true });
    else if (state.status === "signed-out") router.navigate(signInPath(null, location.pathname + location.search), { replace: true });
  }, [state, location]);

  // FLOW-002's landing branches: exactly once, right when the app mounts already signed in at the
  // bare root with no deep link in play (a fresh sign-in with no `returnTo`, or the bare host
  // revisited), a billing or installer principal lands on their own node instead of Home. Gated on
  // a ref, not on `state`/`location`, so a later click on the Home nav link — same address, same
  // session — is a deliberate navigation this never overrides.
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current) return;
    landedRef.current = true;
    if (session && location.pathname === HOME_PATH) {
      const target = landingPath(session);
      if (target !== HOME_PATH) router.navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routed = resolution.kind === "screen" || resolution.kind === "forbidden" ? resolution.route : null;
  const nav = session ? navEntries(ROUTES, session) : [];
  const trail = breadcrumbTrail({
    nav: routed?.nav ?? null,
    title: routed?.title ?? "Not found",
    navHref: routed ? (nav.find((entry) => entry.nav === routed.nav)?.href ?? null) : null,
    tail: resolution.kind === "screen" ? crumbTail : null,
  });
  const title = isPublic && routed ? `${routed.title} · Nexus by AllCode` : documentTitle(trail);
  useEffect(() => {
    document.title = title;
  }, [title]);

  const services: SessionServices = {
    state,
    async signOut() {
      try {
        await signOut((input, init) => fetch(input, init), csrf);
      } catch {
        // The session may still be live server-side: say so rather than pretend to be signed out.
        setActionError(new NetworkError());
        return;
      }
      setState({ status: "signed-out" });
      router.navigate(signInPath("logged_out", null));
    },
  };

  const retry = () => void loadSession(client, csrf).then(setState);

  let page;
  if (state.status === "unreachable") {
    page = (
      <main class="signin">
        <section class="signin-card">
          <div class="signin-brand">
            <LogoMark class="signin-mark" />
            <p class="wordmark">
              <Lockup size="lg" />
            </p>
          </div>
          <ErrorBanner error={state.error} onRetry={retry} />
        </section>
      </main>
    );
  } else if (redirect !== null) {
    page = <Loading label="Opening…" />;
  } else if (isPublic && resolution.kind === "screen") {
    const Screen = resolution.route.component;
    page = <Screen key={location.pathname + location.search} params={resolution.params} query={resolution.query} />;
  } else if (session === null) {
    page = <Loading label="Redirecting to sign in…" />;
  } else {
    page = (
      <Shell
        me={session.me}
        nav={nav}
        currentNav={routed?.nav ?? null}
        trail={trail}
        environment={environment}
        onSignOut={() => void services.signOut()}
      >
        {actionError && (
          <ErrorBanner
            error={actionError}
            onRetry={() => {
              setActionError(null);
              void services.signOut();
            }}
          />
        )}
        {resolution.kind === "screen" && <resolution.route.component params={resolution.params} query={resolution.query} />}
        {resolution.kind === "forbidden" && <Forbidden what={resolution.route.title} />}
        {resolution.kind === "not-found" && <NotFound />}
      </Shell>
    );
  }

  return (
    <ApiContext.Provider value={client}>
      <RouterContext.Provider value={router}>
        <HostContext.Provider value={adminHost}>
          <SessionContext.Provider value={services}>
            <ScopeProvider me={scope}>
              <BreadcrumbContext.Provider value={setCrumbTail}>
                <CustomerDirectoryProvider session={session}>
                  <HelpProvider>{page}</HelpProvider>
                </CustomerDirectoryProvider>
              </BreadcrumbContext.Provider>
            </ScopeProvider>
          </SessionContext.Provider>
        </HostContext.Provider>
      </RouterContext.Provider>
    </ApiContext.Provider>
  );
}

async function start(mount: HTMLElement): Promise<void> {
  render(<Loading label="Loading…" />, mount);
  const initial = await loadSession(client, csrf);
  // FLOW-002's landing branch, decided before the first render so Overview never mounts (and
  // starts its requests) only to be replaced by Invoices or Installer at once.
  if (initial.status === "signed-in" && window.location.pathname === HOME_PATH) {
    const target = landingPath(initial.session);
    if (target !== HOME_PATH) router.navigate(target, { replace: true });
  }
  render(<App initial={initial} />, mount);
}

const mount = document.getElementById("app");
if (mount) void start(mount);
